<template>
  <!-- A real two-level list, not role="tree": a tree promises expand/collapse and arrow-key
       navigation that this read-only view does not have. The chrome matches the คีย์ลัด /
       หมวดหมู่ lists (sticky head, 56px hairline rows) so this page sits in the same settings
       family as /settings/tag and /settings/message-template. -->
  <div class="v2026-topic" :class="{ 'is-narrow': narrow }">
    <div class="v2026-topic-head" aria-hidden="true">
      <span class="v2026-topic-head__name">หัวข้อ</span>
      <span>สถานะ</span>
    </div>

    <ul class="v2026-topic-list">
      <li v-for="node in nodes" :key="node.id" class="v2026-topic-group">
        <div class="v2026-topic-row v2026-topic-row--parent">
          <span class="v2026-topic-chip v2026-topic-chip--topic"
                :style="chipStyle(node.color)"
                aria-hidden="true"></span>
          <span class="v2026-topic-name v2026-topic-name--parent" :title="node.name">{{ node.name }}</span>
          <span class="v2026-topic-badge" :class="node.can_select ? 'is-on' : 'is-off'">
            <span class="v2026-topic-badge__dot" aria-hidden="true"></span>
            {{ node.can_select ? 'กดเลือกได้' : 'กดเลือกไม่ได้' }}
          </span>
        </div>

        <ul v-if="node.child_tags && node.child_tags.length" class="v2026-topic-children">
          <li v-for="child in node.child_tags" :key="child.id" class="v2026-topic-row v2026-topic-row--child">
            <span class="v2026-topic-chip v2026-topic-chip--tag"
                  :style="chipStyle(child.color)"
                  aria-hidden="true"></span>
            <span class="v2026-topic-name" :title="child.name">{{ child.name }}</span>
            <span class="v2026-topic-badge" :class="child.can_select ? 'is-on' : 'is-off'">
              <span class="v2026-topic-badge__dot" aria-hidden="true"></span>
              {{ child.can_select ? 'กดเลือกได้' : 'กดเลือกไม่ได้' }}
            </span>
          </li>
        </ul>
      </li>
    </ul>
  </div>
</template>

<script>
export default {
  name: 'tag-topic-tree-v2026',
  props: {
    // multiLevelTags, exactly as SET_SYSTEM_TAGS shapes it. Two levels only — a grandchild is
    // dropped by the store before it reaches here and this component does not paper over that.
    nodes: {
      type: Array,
      default: () => []
    },
    narrow: {
      type: Boolean,
      default: false
    }
  },
  methods: {
    chipStyle (color) {
      return {
        backgroundColor: color || '#546E7A',
        // The glyph is white on a dark topic and near-black on a pale one, so a pale topic is not
        // a blank square. 0.215 is where #FFFFFF and #212529 contrast equally with the background.
        color: this.glyphOn(color)
      }
    },
    glyphOn (hex) {
      const l = this.relativeLuminance(hex)
      return l > 0.215 ? '#212529' : '#ffffff'
    },
    relativeLuminance (hex) {
      const raw = String(hex || '').trim().replace('#', '')
      let full = raw
      if (raw.length === 3) full = raw[0] + raw[0] + raw[1] + raw[1] + raw[2] + raw[2]
      if (full.length !== 6 || !/^[0-9a-fA-F]{6}$/.test(full)) return 0
      const channel = (v) => {
        const c = parseInt(v, 16) / 255
        return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
      }
      return 0.2126 * channel(full.slice(0, 2)) +
        0.7152 * channel(full.slice(2, 4)) +
        0.0722 * channel(full.slice(4, 6))
    }
  }
}
</script>

<style lang="scss" scoped>

/* Only the ten --v2026-color-* tokens declared on .v2026-shell resolve under /settings, so every
   grey below is hard-coded on purpose. Column recipe copied from .v2026-cat-list / .v2026-mt-list:
   sticky 40px head, 56px rows, #e9ecef hairlines, 8px inline padding. */
.v2026-topic {
  --topic-cols: 40px minmax(0, 1fr) 132px;
  border-top: 1px solid #e9ecef;
}

.v2026-topic-head {
  position: sticky;
  top: 0;
  z-index: 2;
  display: grid;
  grid-template-columns: var(--topic-cols);
  column-gap: 12px;
  align-items: center;
  height: 40px;
  padding: 0 8px;
  background: #ffffff;
  border-bottom: 1px solid #e9ecef;
  font-size: 12px;
  font-weight: 600;
  color: #66696C;
}

.v2026-topic-head__name { grid-column: 2; }

.v2026-topic-list {
  margin: 0;
  padding: 0;
  list-style: none;
}

.v2026-topic-group {
  margin: 0;
}

.v2026-topic-children {
  margin: 0;
  padding: 0;
  list-style: none;
}

/* Read-only means read-only: no hover background, no pointer cursor, no row actions. A hover
   affordance here would promise an edit endpoint that does not exist. */
.v2026-topic-row {
  display: grid;
  grid-template-columns: var(--topic-cols);
  column-gap: 12px;
  align-items: center;
  min-height: 56px;
  padding: 10px 8px;
  border-bottom: 1px solid #e9ecef;
}

.v2026-topic-group:last-child > .v2026-topic-row--parent:only-child,
.v2026-topic-group:last-child .v2026-topic-children .v2026-topic-row:last-child {
  border-bottom: 0;
}

.v2026-topic-row--child {
  /* Indent the whole child row rather than shifting the 32px chip inside a 40px column
     (that overflowed into the name). Status pills on children sit 32px right of the parent
     column — there is no edit endpoint, so column-perfect alignment is not worth a 4-col grid. */
  padding-left: 40px;
}

.v2026-topic-chip {
  width: 32px;
  height: 32px;
  justify-self: center;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  /* A white or near-white topic is still a visible square and not a hole in the row. */
  box-shadow: inset 0 0 0 1px rgba(0, 0, 0, .08);

  &::before {
    content: '';
    width: 16px;
    height: 16px;
    background-color: currentColor;
    -webkit-mask-repeat: no-repeat;
            mask-repeat: no-repeat;
    -webkit-mask-position: center;
            mask-position: center;
    -webkit-mask-size: contain;
            mask-size: contain;
  }
}

.v2026-topic-chip--topic::before {
  -webkit-mask-image: url('~@/assets/icons2026/topic.svg');
          mask-image: url('~@/assets/icons2026/topic.svg');
}

.v2026-topic-chip--tag::before {
  -webkit-mask-image: url('~@/assets/icons2026/tag.svg');
          mask-image: url('~@/assets/icons2026/tag.svg');
}

.v2026-topic-name {
  min-width: 0;
  font-size: 14px;
  color: #212529;
  word-break: break-word;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.v2026-topic-name--parent { font-weight: 600; }

.v2026-topic-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 24px;
  padding: 0 10px;
  border-radius: 999px;
  font-size: 12px;
  white-space: nowrap;
  justify-self: start;

  &__dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: currentColor;
  }

  /* กดเลือกไม่ได้ is not an error — a main topic with children is supposed to be unselectable —
     so the off state is neutral grey, not danger red. The words carry the state, not the colour. */
  &.is-on { background: #ECFDF5; color: #15794F; }
  &.is-off { background: #F2F4F7; color: #66696C; }
}

.v2026-topic.is-narrow {
  --topic-cols: 40px minmax(0, 1fr);

  .v2026-topic-head { display: none; }

  .v2026-topic-row {
    grid-template-areas: "chip name" ". badge";
    row-gap: 4px;
    align-items: start;
  }

  .v2026-topic-chip { grid-area: chip; }
  .v2026-topic-name { grid-area: name; align-self: center; }
  .v2026-topic-badge { grid-area: badge; justify-self: start; }

  .v2026-topic-row--child { padding-left: 28px; }
}
</style>
